/* VULGAR:ERRORS — premium motion (GSAP + ScrollTrigger + Lenis).
   Serotoninn-grade: smooth momentum scroll, hero parallax, line-mask
   headline reveals, staggered card reveals. Built to the gsap-scrolltrigger
   / gsap-timeline / gsap-performance skill patterns.

   Safety: self-bootstraps GSAP + ScrollTrigger + Lenis from CDN; any failure
   degrades to a fully functional static site. Hidden states are set ONLY
   after GSAP confirms loaded. Gated behind prefers-reduced-motion. */
(function () {
  var GSAP_URL = 'https://cdn.jsdelivr.net/npm/gsap@3.12.7/dist/gsap.min.js';
  var ST_URL = 'https://cdn.jsdelivr.net/npm/gsap@3.12.7/dist/ScrollTrigger.min.js';
  var LENIS_URL = 'https://cdn.jsdelivr.net/npm/lenis@1.1.14/dist/lenis.min.js';

  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  function loadScript(src, optional) {
    return new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = src; s.async = true;
      s.onload = resolve;
      s.onerror = optional ? resolve : reject;
      document.head.appendChild(s);
    });
  }

  function setupLenis(gsap, ST) {
    if (!window.Lenis) return;
    try {
      var lenis = new window.Lenis({ lerp: 0.1, wheelMultiplier: 1, smoothWheel: true });
      lenis.on('scroll', ST.update);
      gsap.ticker.add(function (time) { lenis.raf(time * 1000); });
      gsap.ticker.lagSmoothing(0);
      document.documentElement.classList.add('ve-lenis');
      document.querySelectorAll('cart-drawer, .drawer__inner, .cart-drawer, .menu-drawer, .cart-notification, [data-lenis-prevent]')
        .forEach(function (el) { el.setAttribute('data-lenis-prevent', ''); });
      window.veLenis = lenis;
    } catch (e) {}
  }

  /* ── Line-mask headline reveal ──
     Each headline "line" is wrapped in an overflow-hidden clip, then slides
     up from below (yPercent 120 → 0) with a slight per-line stagger when its
     group enters view. Lines that share a parent cascade together. Overflow
     is released on completion so descenders/letter-spacing never clip. */
  function buildHeadlineReveals(gsap, ST) {
    var groupSel = '.ve-gh__title, .ve-world__open-title, .ve-row2__type, .ve-split__headline, .mf-hero, .featured-collection .title-wrapper, .title-wrapper, .rich-text, .collection-hero__title-wrapper';
    var lineSel = '.ve-gh__title > span, .ve-world__open-title, .ve-row2__title, .ve-row2__title--out, .ve-split__line, .ve-split__line--outline, .ve-split__line--sm, .mf-hero-line, .mf-hero-line-outline, .title-wrapper h2, .rich-text__heading, .collection-hero__title, .product__title';

    // Map each line element to its nearest reveal "group" so lines cascade.
    var seen = [];
    var map = new Map();
    gsap.utils.toArray(lineSel).forEach(function (line) {
      if (seen.indexOf(line) > -1) return;
      seen.push(line);
      var group = line.closest(groupSel) || line.parentNode || line;
      if (!map.has(group)) map.set(group, []);
      map.get(group).push(line);
    });

    map.forEach(function (lines, group) {
      var wraps = [];
      lines.forEach(function (line) {
        var w = document.createElement('span');
        w.className = 've-lmask';
        w.style.display = 'block';
        w.style.overflow = 'hidden';
        w.style.paddingBottom = '0.02em';
        line.parentNode.insertBefore(w, line);
        w.appendChild(line);
        line.style.display = 'block';
        wraps.push(w);
      });
      gsap.set(lines, { yPercent: 120 });
      ST.create({
        trigger: group,
        start: 'top 88%',
        once: true,
        onEnter: function () {
          gsap.to(lines, {
            yPercent: 0, duration: 1.05, ease: 'power4.out', stagger: 0.1,
            onComplete: function () { wraps.forEach(function (w) { w.style.overflow = 'visible'; }); },
          });
        },
      });
    });
  }

  function init() {
    if (!window.gsap || !window.ScrollTrigger) return;
    var gsap = window.gsap;
    var ST = window.ScrollTrigger;
    gsap.registerPlugin(ST);
    setupLenis(gsap, ST);

    var mm = gsap.matchMedia();
    mm.add('(prefers-reduced-motion: no-preference)', function () {
      /* Hero parallax (slideshow-based heroes, if any) */
      gsap.utils.toArray('.slideshow .banner__media > img, .banner .banner__media > img, .slideshow__media img').forEach(function (img) {
        gsap.set(img, { scale: 1.14, transformOrigin: 'center center' });
        gsap.to(img, {
          yPercent: 12, ease: 'none',
          scrollTrigger: { trigger: img.closest('.banner, .slideshow, .slideshow__slide') || img, start: 'top top', end: 'bottom top', scrub: 1 },
        });
      });

      /* Line-mask headline reveals (the signature move) */
      buildHeadlineReveals(gsap, ST);

      /* Product cards: staggered rise + fade as they enter */
      var cards = gsap.utils.toArray('.ve-card');
      if (cards.length) {
        gsap.set(cards, { autoAlpha: 0, y: 34 });
        ST.batch('.ve-card', {
          start: 'top 92%',
          onEnter: function (batch) {
            gsap.to(batch, { autoAlpha: 1, y: 0, duration: 0.6, ease: 'power3.out', stagger: 0.08, overwrite: true });
          },
        });
      }

      /* Custom homepage bands: lift in (the band shell, not its headline) */
      var riseSel = ['.ve-world__open-right', '.ve-row2__photo', '.ve-split__photo', '.ve-stmt', '.ve-rules', '.ve-quote'].join(',');
      gsap.utils.toArray(riseSel).forEach(function (el) {
        gsap.from(el, {
          autoAlpha: 0, y: 40, duration: 0.75, ease: 'power3.out',
          scrollTrigger: { trigger: el, start: 'top 84%', once: true },
        });
      });

      /* Section media: gentle scrub parallax on featured imagery */
      gsap.utils.toArray('.image-with-text__media img, .multicolumn-card__image-wrapper img, .collage img').forEach(function (img) {
        gsap.set(img, { scale: 1.1 });
        gsap.to(img, {
          yPercent: 6, ease: 'none',
          scrollTrigger: { trigger: img, start: 'top bottom', end: 'bottom top', scrub: 1.2 },
        });
      });

      ST.refresh();
    });
  }

  function boot() {
    loadScript(GSAP_URL)
      .then(function () { return loadScript(ST_URL); })
      .then(function () { return loadScript(LENIS_URL, true); })
      .then(init)
      .catch(function () { /* CDN down — site stays functional, static */ });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
